/** @format */

import React, { useState } from "react";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { CartItem } from "../types";
import {
  TrashIcon,
  ShoppingBagIcon,
  XMarkIcon,
  CreditCardIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import IOSCheckoutModal from './IOSCheckoutModal';
import { shouldShowIOSWebViewAlert } from '../utils/deviceDetection';

const CartSidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    cartItems, 
    removeFromCart, 
    clearCart, 
    getCartTotal, 
    getCartSubtotal,
    loading, 
    isCartOpen, 
    closeCart 
  } = useCart();
  const { currentUser } = useAuth();
  const [showIOSModal, setShowIOSModal] = useState(false);

  // Determine the best back navigation path
  const getBackNavigation = () => {
    // Check if there's a previous page in history
    if (location.state?.from) {
      return { path: location.state.from, label: "Go Back" };
    }

    // Analyze cart contents to suggest the most appropriate navigation
    const hasCourses = cartItems.some(item => item.type === "course");
    const hasBooks = cartItems.some(item => item.type === "book");
    const hasVideos = cartItems.some(item => item.type === "video");

    if (hasCourses && !hasBooks && !hasVideos) {
      return { path: "/app/catalog", label: "Course Catalog" };
    } else if (hasBooks && !hasCourses && !hasVideos) {
      return { path: "/app/shop", label: "Shop" };
    } else if (hasVideos && !hasCourses && !hasBooks) {
      return { path: "/app/catalog", label: "Course Catalog" };
    } else if (hasCourses && hasBooks) {
      // Mixed cart - go to catalog as it's more comprehensive
      return { path: "/app/catalog", label: "Course Catalog" };
    } else {
      // Default fallback
      return { path: "/app/catalog", label: "Course Catalog" };
    }
  };

  const backNavigation = getBackNavigation();

  const handleRemoveItem = (itemId: string) => {
    removeFromCart(itemId);
  };

  const handleClearCart = () => {
    if (cartItems.length === 0) return;

    if (window.confirm("Are you sure you want to clear your cart?")) {
      clearCart();
    }
  };

  const handleCheckout = () => {
    if (!currentUser) {
      toast.error("Please sign in to checkout");
      return;
    }

    if (cartItems.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    // Check if iOS WebView and show modal instead of direct navigation
    if (shouldShowIOSWebViewAlert()) {
      setShowIOSModal(true);
      return;
    }

    closeCart();
    navigate('/app/checkout');
  };

  const handleViewCart = () => {
    closeCart();
    navigate('/app/cart', { state: { from: location.pathname } });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-GH", {
      style: "currency",
      currency: "GHS",
      minimumFractionDigits: 2,
    }).format(price);
  };

  const getItemImage = (item: any) => {
    if (item.image) return item.image;

    // Default images based on type
    switch (item.type) {
      case "course":
        return "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=150&h=150&fit=crop";
      case "book":
        return "https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=150&h=150&fit=crop";
      case "video":
        return "https://images.unsplash.com/photo-1574717024653-61fd2cb4c2be?w=150&h=150&fit=crop";
      default:
        return "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=150&h=150&fit=crop";
    }
  };

  // Hide cart completely on iOS WebView
  if (!isCartOpen || shouldShowIOSWebViewAlert()) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[70]"
        onClick={closeCart}
      />
      
      {/* Cart Sidebar */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-[70] transform transition-transform duration-300 ease-in-out">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex flex-col">
              <h2 className="text-lg font-semibold text-gray-900">
                Shopping Cart ({cartItems.length})
              </h2>
            </div>
            <button
              onClick={closeCart}
              className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading cart...</p>
              </div>
            ) : cartItems.length === 0 ? (
              <div className="text-center py-8">
                <ShoppingBagIcon className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Your cart is empty
                </h3>
                <p className="text-gray-600 mb-4">
                  Add some items to get started
                </p>
                <button
                  onClick={() => {
                    closeCart();
                    navigate(backNavigation.path);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  Continue Shopping
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {cartItems.map((item: CartItem) => (
                  <div key={item.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    {/* Item Image */}
                    <div className="flex-shrink-0">
                      <img
                        src={getItemImage(item)}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                    </div>

                    {/* Item Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-1 mb-1">
                        <h4 className="text-sm font-medium text-gray-900 truncate">
                          {item.name}
                        </h4>
                      </div>
                      <p className="text-xs text-gray-500 capitalize">
                        {item.type}
                      </p>
                      <p className="text-sm font-semibold text-gray-900">
                        {formatPrice(item.price)}
                      </p>
                    </div>

                    {/* Remove Button */}
                    <button
                      onClick={() => handleRemoveItem(item.id)}
                      className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50"
                      title="Remove item"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {cartItems.length > 0 && (
            <div className="border-t border-gray-200 p-4 space-y-3">

              {/* Total */}
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-900">Total</span>
                <span className="text-lg font-semibold text-gray-900">
                  {formatPrice(getCartTotal())}
                </span>
              </div>
              

              {/* Actions */}
              <div className="space-y-2">
                <button
                  onClick={handleCheckout}
                  disabled={!currentUser}
                  className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  <CreditCardIcon className="h-4 w-4 mr-2" />
                  {currentUser ? (
                    shouldShowIOSWebViewAlert() ? "Checkout Online" : "Checkout"
                  ) : (
                    "Sign in to Checkout"
                  )}
                </button>

                <button
                  onClick={handleViewCart}
                  className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <ArrowRightIcon className="h-4 w-4 mr-2" />
                  View Full Cart
                </button>

                {cartItems.length > 1 && (
                  <button
                    onClick={handleClearCart}
                    className="w-full flex items-center justify-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                  >
                    <TrashIcon className="h-4 w-4 mr-2" />
                    Clear Cart
                  </button>
                )}
              </div>

              {!currentUser && !shouldShowIOSWebViewAlert() && (
                <p className="text-xs text-gray-500 text-center">
                  Please sign in to complete your purchase
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* iOS Checkout Modal */}
      <IOSCheckoutModal
        isOpen={showIOSModal}
        onClose={() => setShowIOSModal(false)}
        cartItems={cartItems}
        cartTotal={getCartTotal()}
        customerInfo={currentUser}
      />
    </>
  );
};

export default CartSidebar;
